import React, { useState } from 'react';
import { SignInWithApple, SignInWithAppleResponse } from '@capacitor-community/apple-sign-in';
import { Capacitor } from '@capacitor/core';

interface AppleSignInProps {
  onSuccess: (response: any) => void;
  onError: (error: string) => void;
  text?: string;
}

const AppleSignIn: React.FC<AppleSignInProps> = ({ 
  onSuccess, 
  onError,
  text = 'Sign in with Apple'
}) => {
  const [loading, setLoading] = useState(false);

  const handleAppleSignIn = async () => {
    // Only available on iOS
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      onError('Sign in with Apple is only available on iOS devices');
      return;
    }

    try {
      setLoading(true);

      console.log('[AppleSignIn] Starting authorization...');
      const result = await SignInWithApple.authorize({
        clientId: 'com.wanderingsock.app',
        redirectURI: 'https://wanderingsock.com/auth/apple/callback',
        scopes: 'email name',
        state: Math.random().toString(36).substring(7),
        nonce: Math.random().toString(36).substring(7),
      });

      console.log('[AppleSignIn] Authorization result:', JSON.stringify(result, null, 2));
      console.log('[AppleSignIn] Result keys:', Object.keys(result));
      
      // The plugin returns { response: { identityToken, user, ... } }
      if (!result.response) {
        throw new Error('No response object received from Apple');
      }
      
      console.log('[AppleSignIn] Response keys:', Object.keys(result.response));
      console.log('[AppleSignIn] Has identityToken:', !!result.response.identityToken);
      console.log('[AppleSignIn] Has user:', !!result.response.user);
      
      if (!result.response.identityToken) {
        throw new Error('No identity token received from Apple');
      }

      onSuccess(result.response);
    } catch (error: any) {
      console.error('[AppleSignIn] Error:', error);
      console.error('[AppleSignIn] Error details:', JSON.stringify(error, null, 2));
      
      // Error codes that should be silently handled (user dismissed/canceled):
      // 1000 = ASAuthorizationErrorCanceled (user explicitly canceled)
      // 1001 = ASAuthorizationErrorUnknown (dialog dismissed, network issues, etc.)
      // https://developer.apple.com/documentation/authenticationservices/asauthorizationerror
      const errorString = String(error.message || error.code || error);
      const isCanceled = errorString.includes('1000') || 
                        errorString.includes('1001') ||
                        errorString.toLowerCase().includes('cancel') ||
                        errorString.toLowerCase().includes('user_cancelled') ||
                        errorString.toLowerCase().includes('user cancelled') ||
                        error.code === '1000' || 
                        error.code === 1000 ||
                        error.code === '1001' || 
                        error.code === 1001 ||
                        error.errorCode === 1000 ||
                        error.errorCode === '1000' ||
                        error.errorCode === 1001 ||
                        error.errorCode === '1001';
      
      if (!isCanceled) {
        onError(error.message || 'Apple Sign-In failed');
      }
      // If canceled, just silently return - user chose not to sign in
    } finally {
      setLoading(false);
    }
  };

  // Only show on iOS
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
    return null;
  }

  return (
    <button
      type="button"
      onClick={handleAppleSignIn}
      disabled={loading}
      className="apple-signin-btn"
      style={{
        width: '100%',
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.6 : 1,
      }}
    >
      {/* Apple logo SVG */}
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
      </svg>
      {loading ? 'Signing in...' : text}
    </button>
  );
};

export default AppleSignIn;
